import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrendingUp, Shield, DollarSign, Clock, Users, Building, Star, CheckCircle, BarChart3, ArrowRight } from "lucide-react";
import Navigation from "@/components/Navigation";
import SEOHead from "@/components/SEOHead";

const InvestorSalesPitch = () => {
  const keyBenefits = [
    {
      icon: <TrendingUp className="h-12 w-12 text-primary" />,
      title: "Superior Returns",
      subtitle: "8-12% Annual Yields",
      description: "Consistently outperform traditional investments with asset-backed security."
    },
    {
      icon: <Shield className="h-12 w-12 text-primary" />,
      title: "Real Estate Backed",
      subtitle: "Tangible Security",
      description: "Every investment secured by actual property - your safety net in any market."
    },
    {
      icon: <DollarSign className="h-12 w-12 text-primary" />,
      title: "Monthly Cash Flow",
      subtitle: "Predictable Income",
      description: "Receive steady monthly payments that you can count on, month after month."
    },
    {
      icon: <Clock className="h-12 w-12 text-primary" />,
      title: "100% Passive",
      subtitle: "Zero Management",
      description: "Earn while you sleep. No tenants, no repairs, no property management headaches."
    }
  ];

  const competitiveAdvantages = [
    "8-12% annual returns vs 0.5-1% savings accounts",
    "Monthly income vs quarterly stock dividends",
    "Asset-backed security vs volatile stock prices",
    "No management required vs rental property hassles",
    "Stable real estate market vs unpredictable crypto",
    "Helping families achieve homeownership"
  ];

  const investmentProcess = [
    {
      step: "01",
      title: "Review Opportunities",
      description: "Access our carefully vetted land contract investments with full transparency"
    },
    {
      step: "02", 
      title: "Due Diligence Complete",
      description: "We handle all property research, title work, and legal documentation"
    },
    {
      step: "03",
      title: "Secure Your Investment",
      description: "Invest with confidence knowing your money is protected by real estate"
    },
    {
      step: "04",
      title: "Receive Monthly Returns",
      description: "Enjoy consistent monthly payments deposited directly to your account"
    }
  ];

  return (
    <>
      <SEOHead 
        title="Exclusive Investor Opportunities - Land Contract Investments"
        description="Discover high-yield land contract investments offering 8-12% annual returns with real estate security. Join exclusive investor network."
        keywords="land contract investment, real estate investing, high yield returns, passive income, investor opportunities"
        canonicalUrl="https://contractfundinggroup.com/investor-sales-pitch"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-primary/5">
        <Navigation />
        
        <div className="py-12 px-4">
          <div className="max-w-6xl mx-auto">
            
            {/* Hero Section */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Star className="h-4 w-4 mr-2" />
                Exclusive Investor Opportunity
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
                Real Estate's Hidden 
                <span className="text-primary block">Gold Mine</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                While others struggle with 1% savings rates and volatile stocks, smart investors are earning 
                <span className="font-bold text-primary"> 8-12% annual returns</span> with the safety of real estate backing.
              </p>
              
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="text-lg px-8 py-6">
                  View Current Opportunities
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button variant="outline" size="lg" className="text-lg px-8 py-6">
                  Schedule Private Consultation
                </Button>
              </div>
            </div>

            {/* Key Benefits Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {keyBenefits.map((benefit, index) => (
                <Card key={index} className="text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <CardHeader className="pb-4">
                    <div className="flex justify-center mb-4">
                      {benefit.icon}
                    </div>
                    <CardTitle className="text-xl mb-2">{benefit.title}</CardTitle>
                    <div className="text-primary font-bold text-lg">{benefit.subtitle}</div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Why Choose Us Section */}
            <Card className="mb-16 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
              <CardHeader className="text-center">
                <CardTitle className="text-3xl text-foreground mb-4">
                  Why Land Contracts Outperform Everything Else
                </CardTitle>
                <p className="text-muted-foreground text-lg">
                  The investment opportunity Wall Street doesn't want you to know about
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-xl font-bold mb-4 text-foreground">Competitive Advantages:</h4>
                    <div className="space-y-3">
                      {competitiveAdvantages.map((advantage, index) => (
                        <div key={index} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{advantage}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-background/50 p-6 rounded-lg">
                    <h4 className="text-xl font-bold mb-4 text-foreground">Investment Comparison</h4>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Land Contracts</span>
                        <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full font-bold">8-12%</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Stock Market</span>
                        <span className="text-muted-foreground">7-10% (volatile)</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Bonds</span>
                        <span className="text-muted-foreground">2-4%</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Savings Account</span>
                        <span className="text-muted-foreground">0.5-1%</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Rental Property</span>
                        <span className="text-muted-foreground">6-8% + hassles</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Investment Process */}
            <div className="mb-16">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Your Path to Passive Wealth
                </h2>
                <p className="text-xl text-muted-foreground">
                  Simple. Secure. Profitable.
                </p>
              </div>
              
              <div className="grid md:grid-cols-4 gap-6">
                {investmentProcess.map((process, index) => (
                  <div key={index} className="text-center relative">
                    <div className="bg-primary text-primary-foreground w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                      {process.step}
                    </div>
                    <h4 className="text-lg font-bold mb-2 text-foreground">{process.title}</h4>
                    <p className="text-muted-foreground text-sm">{process.description}</p>
                    
                    {index < investmentProcess.length - 1 && (
                      <ArrowRight className="hidden md:block absolute top-8 -right-3 h-6 w-6 text-primary" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Social Proof & CTA */}
            <Card className="bg-gradient-to-r from-primary to-secondary text-primary-foreground">
              <CardContent className="text-center py-12">
                <BarChart3 className="h-16 w-16 mx-auto mb-6 opacity-90" />
                <h2 className="text-3xl font-bold mb-4">
                  Join Our Exclusive Investor Network
                </h2>
                <p className="text-xl mb-2 opacity-90">
                  Limited opportunities. Unlimited potential.
                </p>
                <p className="mb-8 opacity-80 max-w-2xl mx-auto">
                  Our carefully selected land contract investments have consistently delivered returns 
                  while helping families achieve the American dream of homeownership.
                </p>
                
                <div className="grid md:grid-cols-3 gap-6 mb-8 max-w-3xl mx-auto">
                  <div>
                    <div className="text-3xl font-bold">$2.5M+</div>
                    <div className="opacity-80">Total Investments Funded</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold">98%</div>
                    <div className="opacity-80">On-Time Payment Rate</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold">150+</div>
                    <div className="opacity-80">Families Helped</div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <Button size="lg" variant="secondary" className="text-lg px-8 py-6">
                    Access Investment Portal
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <p className="text-sm opacity-80">
                    Minimum investment: $15,000 • Qualified investors only
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Contact Footer */}
            <div className="text-center mt-12 py-8 border-t border-border">
              <p className="text-muted-foreground mb-4">
                Ready to discuss your investment goals? Let's talk.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="outline">
                  Schedule Consultation
                </Button>
                <Button variant="outline">
                  Download Investment Guide
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InvestorSalesPitch;